import { message } from 'antd'

const API_BASE_URL = (import.meta as any).env?.VITE_API_URL || ''

type ClientErrorPayload = {
  source?: string
  service?: string
  level?: string
  environment?: string
  release?: string
  message: string
  stack?: string
  url?: string
  user_agent?: string
  occurred_at?: string
  fingerprint?: string
  metadata?: Record<string, unknown>
}

let installed = false
const APP_RELEASE = (import.meta as any).env?.VITE_APP_VERSION || 'dev'
const APP_ENV = (import.meta as any).env?.MODE || 'unknown'

const DYNAMIC_IMPORT_RELOAD_PREFIX = 'dynamic-import-reload:'

const isDynamicImportFailure = (value: unknown) => {
  const text = typeof value === 'string'
    ? value
    : (value as { message?: string } | undefined)?.message || String(value)
  return (
    text.includes('Failed to fetch dynamically imported module') ||
    text.includes('Importing a module script failed')
  )
}

const maybeReloadForDynamicImportFailure = (reason: unknown) => {
  if (typeof window === 'undefined' || !isDynamicImportFailure(reason)) {
    return false
  }
  const reloadKey = `${DYNAMIC_IMPORT_RELOAD_PREFIX}${window.location.pathname}`
  if (sessionStorage.getItem(reloadKey) === '1') {
    return false
  }
  sessionStorage.setItem(reloadKey, '1')
  message.warning('页面资源已更新，正在自动刷新…')
  window.setTimeout(() => {
    window.location.reload()
  }, 200)
  return true
}

export const reportClientError = async (payload: ClientErrorPayload) => {
  try {
    const token = localStorage.getItem('access_token')
    await fetch(`${API_BASE_URL}/api/v1/client-logs/errors`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({
        source: payload.source || 'frontend',
        service: payload.service || 'frontend-web',
        level: payload.level || 'error',
        environment: payload.environment || APP_ENV,
        release: payload.release || APP_RELEASE,
        message: payload.message,
        stack: payload.stack,
        url: payload.url || window.location.href,
        user_agent: payload.user_agent || navigator.userAgent,
        occurred_at: payload.occurred_at || new Date().toISOString(),
        fingerprint: payload.fingerprint,
        metadata: payload.metadata || {},
      }),
      keepalive: true,
    })
  } catch {
    // Ignore reporting failures to avoid recursive noise.
  }
}

export const installGlobalErrorLogging = () => {
  if (installed || typeof window === 'undefined') {
    return
  }
  installed = true

  window.addEventListener('error', (event) => {
    maybeReloadForDynamicImportFailure(event.error || event.message)
    void reportClientError({
      message: event.message || 'Unhandled window error',
      stack: event.error?.stack,
      metadata: {
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
        route: window.location.pathname,
      },
    })
  })

  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason
    maybeReloadForDynamicImportFailure(reason)
    void reportClientError({
      message:
        typeof reason === 'string'
          ? reason
          : reason?.message || 'Unhandled promise rejection',
      stack: reason?.stack,
      metadata: {
        reason: typeof reason === 'string' ? reason : String(reason),
        route: window.location.pathname,
      },
    })
  })
}
